// production rebuild marker: herborist-badge-removed-2026-05-12
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/AuthContext";
import { LanguageProvider } from "./lib/i18n";
import { AdminPreviewProvider } from "./lib/AdminPreviewContext";
import { PricingProvider } from "./lib/PricingContext";
import { CartProvider } from "./hooks/useCart";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import B2BLayout from "./components/B2BLayout";
import Home from "./pages/Home";
import Catalog from "./pages/Catalog";
import ProductDetail from "./pages/ProductDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ConfirmEmailChange from "./pages/ConfirmEmailChange";
import Onboarding from "./pages/Onboarding";
import RegisterB2B from "./pages/RegisterB2B";
import Account from "./pages/Account";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import CheckoutPending from "./pages/CheckoutPending";
import CheckoutFailed from "./pages/CheckoutFailed";
import OrderConfirmation from "./pages/OrderConfirmation";
import AdminProducts from "./pages/admin/Products";
import AdminProductForm from "./pages/admin/ProductForm";
import AdminCategories from "./pages/admin/Categories";
import AdminB2B from "./pages/admin/B2B";
import AdminB2BDetail from "./pages/admin/B2BDetail";
import AdminPrescriberForm from "./pages/admin/PrescriberForm";
import AdminB2BVatAudit from "./pages/admin/B2BVatAudit";
import AdminCredits from "./pages/admin/Credits";
import AdminOrders from "./pages/admin/Orders";
import AdminManualOrderNew from "./pages/admin/ManualOrderNew";
import AdminOrderDetail from "./pages/admin/OrderDetail";
import AdminConfig from "./pages/admin/Config";
import LegacyShopImport from "./pages/admin/LegacyShopImport";
import MyOrderDetail from "./pages/MyOrderDetail";
import B2BDashboard from "./pages/b2b/Dashboard";
import B2BReferrals from "./pages/b2b/Referrals";
import B2BCommissions from "./pages/b2b/Commissions";
import OpsLayout from "./components/OpsLayout";
import OpsDashboard from "./pages/ops/Dashboard";
import OpsOrders from "./pages/ops/Orders";
import OpsOrderDetail from "./pages/ops/OrderDetail";
import OpsInventory from "./pages/ops/Inventory";
import OpsPayments from "./pages/ops/Payments";
import OpsSystem from "./pages/ops/System";
import OpsActiveAnts from "./pages/ops/ActiveAnts";
import OpsShipments from "./pages/ops/Shipments";
import OpsLogs from "./pages/ops/Logs";
import OpsAccounting from "./pages/ops/Accounting";
import OpsAccountingWeeks from "./pages/ops/AccountingWeeks";
import OpsAccountingWeekDetail from "./pages/ops/AccountingWeekDetail";
// ── Public legal pages (2026-05-19 legal/compliance slice) ─────
import AlgemeneVoorwaarden from "./pages/legal/AlgemeneVoorwaarden";
import Privacy from "./pages/legal/Privacy";
import CookiesPolicy from "./pages/legal/Cookies";
import Herroepingsrecht from "./pages/legal/Herroepingsrecht";
import Verzending from "./pages/legal/Verzending";
import LegalContact from "./pages/legal/Contact";

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <AdminPreviewProvider>
          <PricingProvider>
            <CartProvider>
              <BrowserRouter>
                <Routes>
                  <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/catalog" element={<Catalog />} />
                    {/* Dutch-language SEO/bookmark aliases — redirect
                        to the canonical /catalog route. The nav label
                        "Producten" is i18n-only; this just makes
                        external deep-links resolve gracefully. */}
                    <Route path="/producten" element={<Navigate to="/catalog" replace />} />
                    <Route path="/nl/producten" element={<Navigate to="/catalog" replace />} />
                    <Route path="/products/:slug" element={<ProductDetail />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/register/b2b" element={<RegisterB2B />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/account/orders/:orderNumber" element={<MyOrderDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/checkout/pending" element={<CheckoutPending />} />
                    <Route path="/checkout/failed" element={<CheckoutFailed />} />
                    <Route path="/order/:orderNumber/confirmation" element={<OrderConfirmation />} />
                    {/* Public legal / compliance pages */}
                    <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaarden />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/cookies" element={<CookiesPolicy />} />
                    <Route path="/herroepingsrecht" element={<Herroepingsrecht />} />
                    <Route path="/verzending" element={<Verzending />} />
                    <Route path="/contact" element={<LegalContact />} />
                    <Route element={<AdminLayout />}>
                      <Route path="/admin/products" element={<AdminProducts />} />
                      <Route path="/admin/products/legacy-shop" element={<LegacyShopImport />} />
                      <Route path="/admin/products/new" element={<AdminProductForm />} />
                      <Route path="/admin/products/:id/edit" element={<AdminProductForm />} />
                      <Route path="/admin/categories" element={<AdminCategories />} />
                      <Route path="/admin/b2b" element={<AdminB2B />} />
                      <Route path="/admin/b2b/vat-audit" element={<AdminB2BVatAudit />} />
                      <Route path="/admin/b2b/new" element={<AdminPrescriberForm />} />
                      <Route path="/admin/b2b/:id" element={<AdminB2BDetail />} />
                      <Route path="/admin/b2b/:id/edit" element={<AdminPrescriberForm />} />
                      <Route path="/admin/credits" element={<AdminCredits />} />
                      <Route path="/admin/orders" element={<AdminOrders />} />
                      <Route path="/admin/orders/new" element={<AdminManualOrderNew />} />
                      <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
                      <Route path="/admin/config" element={<AdminConfig />} />
                    </Route>
                    <Route element={<B2BLayout />}>
                      <Route path="/b2b" element={<B2BDashboard />} />
                      <Route path="/b2b/referrals" element={<B2BReferrals />} />
                      <Route path="/b2b/commissions" element={<B2BCommissions />} />
                    </Route>
                    <Route element={<OpsLayout />}>
                      <Route path="/ops" element={<OpsDashboard />} />
                      <Route path="/ops/orders" element={<OpsOrders />} />
                      <Route path="/ops/orders/:id" element={<OpsOrderDetail />} />
                      <Route path="/ops/inventory" element={<OpsInventory />} />
                      <Route path="/ops/payments" element={<OpsPayments />} />
                      <Route path="/ops/shipments" element={<OpsShipments />} />
                      <Route path="/ops/active-ants" element={<OpsActiveAnts />} />
                      <Route path="/ops/logs" element={<OpsLogs />} />
                      <Route path="/ops/accounting" element={<OpsAccounting />} />
                      <Route path="/ops/accounting/weeks" element={<OpsAccountingWeeks />} />
                      <Route path="/ops/accounting/weeks/:weekKey" element={<OpsAccountingWeekDetail />} />
                      <Route path="/ops/system" element={<OpsSystem />} />
                    </Route>
                  </Route>
                </Routes>
              </BrowserRouter>
            </CartProvider>
          </PricingProvider>
        </AdminPreviewProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
